import { useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { VisibleAmountSum } from "@/components/ui/VisibleAmountSum";
import {
  RATING_AXES,
  useContractorRatings,
  useRateOrder,
  type AwaitingRating,
} from "@/features/rfq/api/ratings";
import { Stars, StarPicker } from "@/features/rfq/components/Stars";

/**
 * Oceny firmy po zrealizowanych zamówieniach — sekcja na karcie podwykonawcy.
 *
 * Trzy osie zamiast jednej gwiazdki, bo firma bywa świetna technicznie i nie do
 * dogadania. Widoczne wyłącznie dla GK; API i tak odcina podwykonawców.
 */

export function ContractorRatingsSection({ contractorId }: { contractorId: string }) {
  const { data } = useContractorRatings(contractorId);
  const rate = useRateOrder(contractorId);

  const [rating, setRating] = useState<AwaitingRating | null>(null);
  const [scores, setScores] = useState({ quality: 4, cooperation: 4, timeliness: 4 });
  const [comment, setComment] = useState("");

  if (!data) return null;
  const { summary, ratings, awaiting_rating: awaiting } = data;

  const submit = () => {
    if (!rating) return;
    rate.mutate(
      { orderId: rating.id, ...scores, comment },
      {
        onSuccess: () => {
          setRating(null);
          setScores({ quality: 4, cooperation: 4, timeliness: 4 });
          setComment("");
        },
      },
    );
  };

  return (
    <section className="mb-6">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
        <Star size={16} className="text-[var(--color-text-muted)]" />
        Oceny współpracy
      </h2>

      <div className="bg-white p-5 dark:bg-gray-800">
        {/* Podsumowanie */}
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          {summary.count === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              Brak ocen — firma nie ma jeszcze rozliczonego zamówienia.
            </p>
          ) : (
            <>
              <div>
                <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  Średnia
                </p>
                <div className="flex items-center gap-2">
                  <Stars value={summary.overall ?? 0} size={16} />
                  <span className="text-lg font-semibold text-[var(--color-text-primary)]">
                    {summary.overall?.toFixed(2)}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    z {summary.count} {summary.count === 1 ? "oceny" : "ocen"}
                  </span>
                </div>
              </div>
              {RATING_AXES.map((axis) => (
                <div key={axis.key}>
                  <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                    {axis.label}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Stars value={summary[axis.key] ?? 0} />
                    <span className="text-sm text-[var(--color-text-primary)]">
                      {summary[axis.key]?.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
          <div className="ml-auto text-right">
            <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
              W trakcie
            </p>
            <p className="text-lg font-semibold text-[var(--color-text-primary)]">
              {summary.in_progress_orders}
            </p>
          </div>
        </div>

        {/* Czekające na ocenę */}
        {awaiting.length > 0 && (
          <div className="mt-4 border-t border-[var(--color-border)] pt-3">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
              Czeka na ocenę ({awaiting.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {awaiting.map((order) => (
                <Button
                  key={order.id}
                  variant="secondary"
                  className="!py-1 !px-2 !text-xs"
                  onClick={() => setRating(order)}
                >
                  <Star size={12} className="mr-1 inline" /> {order.order_number}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Historia */}
        {ratings.length > 0 && (
          <ul className="mt-4 divide-y divide-[var(--color-border)] border-t border-[var(--color-border)]">
            {ratings.map((entry) => (
              <li key={entry.id} className="py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/orders/${entry.order}`}
                    className="text-sm text-[var(--color-primary)] hover:underline"
                  >
                    {entry.order_number}
                  </Link>
                  <Stars value={entry.average} />
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {new Date(entry.created_at).toLocaleDateString("pl")}
                    {entry.rated_by_name && ` · ${entry.rated_by_name}`}
                  </span>
                  {/* Wartość zamówienia — serwer zeruje pozycje, których oglądający nie
                      ma prawa widzieć, więc bez uprawnień zostaje „—". */}
                  <span
                    className="ml-auto text-sm text-[var(--color-text-primary)]"
                    title="Wartość netto ocenionego zamówienia"
                  >
                    <VisibleAmountSum values={entry.order_line_amounts ?? []} />
                  </span>
                </div>
                <p className="text-xs text-[var(--color-text-secondary)]">
                  {RATING_AXES.map((axis) => `${axis.label}: ${entry[axis.key]}`).join(" · ")}
                </p>
                {entry.comment && (
                  <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                    {entry.comment}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {rating && (
        <Modal open onClose={() => setRating(null)} title={`Ocena: ${rating.order_number}`}>
          <div className="space-y-4">
            {RATING_AXES.map((axis) => (
              <div key={axis.key} className="flex items-center justify-between gap-4">
                <span className="text-sm text-[var(--color-text-primary)]">{axis.label}</span>
                <StarPicker
                  value={scores[axis.key]}
                  onChange={(v) => setScores((prev) => ({ ...prev, [axis.key]: v }))}
                />
              </div>
            ))}
            <div>
              <label className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
                Komentarz (opcjonalnie)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]"
              />
            </div>
            <p className="text-xs text-[var(--color-text-muted)]">
              Ocena jest widoczna wyłącznie dla GK ELEKTIM.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRating(null)}>
                Anuluj
              </Button>
              <Button onClick={submit} disabled={rate.isPending}>
                {rate.isPending ? "Zapisywanie…" : "Zapisz ocenę"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
